import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { Box, Stack, Typography, Paper, Button, FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Chip, IconButton, Tooltip, Grid, Autocomplete, Checkbox } from '@mui/material'
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import { useTheme } from '@mui/material/styles'

import AddCircleOutline from '@mui/icons-material/AddCircleOutline'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import EditOutlined from '@mui/icons-material/EditOutlined'
import ArrowUpward from '@mui/icons-material/ArrowUpward'
import ArrowDownward from '@mui/icons-material/ArrowDownward'
import DownloadIcon from '@mui/icons-material/Download'
import UploadIcon from '@mui/icons-material/Upload'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import SearchIcon from '@mui/icons-material/Search'
import FilterListIcon from '@mui/icons-material/FilterList'
import ClearIcon from '@mui/icons-material/Clear'
import IosShareIcon from '@mui/icons-material/IosShare'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import apiService from '../services/api.service'
import { useDispatch } from 'react-redux'
import { showSnackbar, setLoading } from '../store/slices/uiSlice'
import FioriSidePanel from '../components/common/FioriSidePanel'
 

type StageConfig = { name: string | null; userIds: number[]; rule: 'ANY'|'ALL'; allocationtype: 'Project'|'CostCenter'; pendingCount?: number }
type FlowRowSimple = {
  id: string
  documenttype: 'PR'|'PO'
  flowname: string
  requisitiontype: 'Inventory'|'Service'|'FixedAsset'
  allocationtype: 'Project'|'CostCenter'
  companyid: number | null | undefined
  stages: StageConfig[]
  initiatoruserids?: number[]
  autoposttosap?: boolean
  facilitytype?: string | null
  cycle?: string | null
  allocationcode?: string | null
}

const reqTypes = ['Inventory','Service','FixedAsset']
const documentTypes: Array<'PR'|'PO'> = ['PR','PO']
const facilityTypes = ['NA','Commercial','Residential']
const cycles = ['Project','Department','HR','Admin']
const readSelectedCompanyId = () => Number(localStorage.getItem('selectedCompanyId') || '') || undefined
const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkboxCheckedIcon = <CheckBoxIcon fontSize="small" />
const parseAllocationCodes = (value?: string | null) => String(value || '')
  .split(',')
  .map((code) => code.trim())
  .filter(Boolean)
const serializeAllocationCodes = (codes: string[]) => Array.from(new Set(codes.map((code) => code.trim()).filter(Boolean))).join(',')
const buildFlowRowId = (row: Pick<FlowRowSimple, 'documenttype'|'flowname'|'requisitiontype'|'allocationtype'|'companyid'|'allocationcode'|'facilitytype'|'cycle'>) => (
  [
    row.companyid ?? 'global',
    row.documenttype || 'PR',
    row.requisitiontype || '',
    row.allocationtype || '',
    row.flowname || '',
    row.allocationcode || '',
    row.facilitytype || '',
    row.cycle || ''
  ].join('|')
)
const getDistributionRuleOption = (rule: any): { code: string; name: string } | null => {
  const data = rule?.data || rule?.Data || {}
  const code = String(
    data?.FactorCode ??
    data?.OcrCode ??
    data?.Code ??
    rule?.FactorCode ??
    rule?.keycode ??
    rule?.KeyCode ??
    ''
  ).trim()
  if (!code) return null
  const name = String(
    data?.FactorDescription ??
    data?.OcrName ??
    data?.Name ??
    rule?.FactorDescription ??
    ''
  ).trim()
  return { code, name }
}
const toStageChipLabel = (s: StageConfig) => {
  const baseName = s.name || (s.userIds.length ? `${s.userIds.length} user(s)` : '-')
  const pendingPart = ` (${s.pendingCount ?? 0})`
  return `${baseName}${pendingPart}`
}

export default function ApprovalFlowSettings() {
  const dispatch = useDispatch()
  const theme = useTheme()
  const [rows, setRows] = useState<FlowRowSimple[]>([])
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | undefined>(() => readSelectedCompanyId())
  const [filters, setFilters] = useState<{
    documenttype?: 'PR'|'PO'
    requisitiontype?: string
    allocationtype?: string
    facilitytype?: string
    cycle?: string
    allocationcode?: string
    searchText?: string
  }>({})
  const [open, setOpen] = useState(false)
  const [editingFlowKey, setEditingFlowKey] = useState<string | null>(null)
  const [form, setForm] = useState<FlowRowSimple>({ id: 'draft', documenttype: 'PR', flowname: '', requisitiontype: 'Inventory', allocationtype: 'Project', companyid: selectedCompanyId ?? null, stages: [], autoposttosap: false, facilitytype: 'NA', cycle: 'Project', allocationcode: null })
  const [allUsers, setAllUsers] = useState<any[]>([])
  const [saving, setSaving] = useState(false)
  const [initiatorUsers, setInitiatorUsers] = useState<number[]>([])
  const [projectOptions, setProjectOptions] = useState<Array<{ code: string; name: string }>>([])
  const [costCenterOptions, setCostCenterOptions] = useState<Array<{ code: string; name: string }>>([])
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 })
  const originalFlowIdentifiersRef = useRef<{
    documentType: 'PR'|'PO'|null
    requisitionType: string | null
    allocationType: string | null
    flowName: string | null
    companyId: number | null
    facilityType: string | null
    cycle: string | null
    allocationCode: string | null
  } | null>(null)
  const projectOptionsLoadedRef = useRef(false)
  const costCenterOptionsLoadedRef = useRef(false)
  const documentTypeOptions = useMemo(() => documentTypes.map((value) => ({ value, label: value })), [])
  const requisitionTypeOptions = useMemo(() => reqTypes.map((value) => ({
    value,
    label: value === 'Inventory' ? 'Material' : value === 'Service' ? 'Service' : 'Asset'
  })), [])
  const allocationTypeOptions = useMemo(() => ([
    { value: 'Project', label: 'Project' },
    { value: 'CostCenter', label: 'Cost Center' }
  ]), [])
  const facilityTypeOptions = useMemo(() => facilityTypes.map((value) => ({ value, label: value })), [])
  const cycleOptions = useMemo(() => cycles.map((value) => ({ value, label: value })), [])
  const userOptions = useMemo(
    () => allUsers.map((u: any) => ({ id: Number(u.UserID ?? u.userid), label: String(u.FullName ?? u.fullname) })).filter((u) => Number.isFinite(u.id)),
    [allUsers]
  )
  const selectedAllocationCodes = useMemo(() => parseAllocationCodes(form.allocationcode), [form.allocationcode])
  const filterSpecificCodeOptions = useMemo(
    () => Array.from(
      new Set(
        rows.flatMap((r) => parseAllocationCodes(r.allocationcode))
      )
    ).sort().map((code) => ({ code, label: code })),
    [rows]
  )

  useEffect(() => {
    const syncSelectedCompany = () => setSelectedCompanyId(readSelectedCompanyId())
    window.addEventListener('company-changed', syncSelectedCompany as EventListener)
    window.addEventListener('focus', syncSelectedCompany)
    return () => {
      window.removeEventListener('company-changed', syncSelectedCompany as EventListener)
      window.removeEventListener('focus', syncSelectedCompany)
    }
  }, [])

  useEffect(() => {
    projectOptionsLoadedRef.current = false
    costCenterOptionsLoadedRef.current = false
    setProjectOptions([])
    setCostCenterOptions([])
    if (!open) {
      setForm((current) => ({ ...current, companyid: selectedCompanyId ?? null }))
    }
  }, [open, selectedCompanyId])





  const filteredRows = useMemo(() => {
    return rows.filter(r => {
      if (filters.documenttype && r.documenttype !== filters.documenttype) return false
      // Filter by requisition type
      if (filters.requisitiontype && r.requisitiontype !== filters.requisitiontype) return false

      // Filter by allocation type
      if (filters.allocationtype && r.allocationtype !== filters.allocationtype) return false

      // Filter by facility type
      if (filters.facilitytype && r.facilitytype !== filters.facilitytype) return false

      // Filter by cycle
      if (filters.cycle && r.cycle !== filters.cycle) return false

      // Filter by allocation code
      if (filters.allocationcode && !parseAllocationCodes(r.allocationcode).includes(filters.allocationcode)) return false

      // Filter by search text (searches in flow name, allocation code, and stage names)
      if (filters.searchText) {
        const searchLower = filters.searchText.toLowerCase()
        const flowNameMatch = (r.flowname || '').toLowerCase().includes(searchLower)
        const allocCodeMatch = parseAllocationCodes(r.allocationcode).some((code) => code.toLowerCase().includes(searchLower))
        const stageNamesMatch = (r.stages || []).some(s => (s.name || '').toLowerCase().includes(searchLower))

        if (!flowNameMatch && !allocCodeMatch && !stageNamesMatch) return false
      }

      return true
    })
  }, [rows, filters])

  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'id',
      headerName: 'SL.NO',
      width: 80,
      valueGetter: (params: any) => {
        const index = filteredRows.findIndex(r => r.id === params.row.id)
        return index + 1
      }
    },
    {
      field: 'flowname',
      headerName: 'Flow Name',
      width: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{params.value || '-'}</Typography>
      )
    },
    {
      field: 'documenttype',
      headerName: 'Document Type',
      width: 130
    },
    {
      field: 'requisitiontype',
      headerName: 'Module',
      width: 120,
      valueGetter: (params: any) => params.value === 'Inventory' ? 'Material' : params.value === 'Service' ? 'Service' : 'Asset'
    },
    {
      field: 'allocationtype',
      headerName: 'Allocation',
      width: 120
    },
    {
      field: 'allocationcode',
      headerName: 'Specific Code',
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        parseAllocationCodes(String(params.value || '')).length ? (
          <Tooltip title={parseAllocationCodes(String(params.value || '')).join(', ')}>
            <Chip
              label={parseAllocationCodes(String(params.value || '')).length === 1
                ? parseAllocationCodes(String(params.value || ''))[0]
                : `${parseAllocationCodes(String(params.value || '')).length} selected`}
              size="small"
              variant="outlined"
              color="primary"
            />
          </Tooltip>
        ) : (
          <Typography variant="body2" color="text.secondary">All</Typography>
        )
      )
    },
    {
      field: 'facilitytype',
      headerName: 'Facility',
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{params.value || 'NA'}</Typography>
      )
    },
    {
      field: 'cycle',
      headerName: 'Cycle',
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{params.value || 'Project'}</Typography>
      )
    },
    {
      field: 'stages',
      headerName: 'Stages',
      width: 350,
      renderCell: (params: GridRenderCellParams) => {
        const stages = params.value as StageConfig[]
        return (
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {(stages || []).map((s, i) => (
              <Tooltip key={`st-${i}`} title={`Stage ${i+1} | Rule: ${s.rule} | Pending: ${s.pendingCount ?? 0} PR${(s.pendingCount ?? 0) !== 1 ? 's' : ''}`}>
                <Chip label={toStageChipLabel(s)} size="small" color={s.rule === 'ALL' ? 'warning' : 'info'} variant={s.name ? 'filled' : 'outlined'} />
              </Tooltip>
            ))}
            {(!stages || stages.length === 0) && (<Typography variant="body2">-</Typography>)}
          </Stack>
        )
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 220,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as FlowRowSimple
        return (
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" size="small" startIcon={<EditOutlined />} onClick={() => openEdit(row)}>Edit</Button>
            <Button variant="outlined" color="error" size="small" startIcon={<DeleteOutline />} onClick={() => remove(row)}>Delete</Button>
          </Stack>
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [filteredRows])

  const load = useCallback(async () => {
    try {
      dispatch(setLoading(true))
      const res = await apiService.listApprovalWorkflows({ documentType: (filters.documenttype || 'PR'), requisitionType: filters.requisitiontype, companyId: selectedCompanyId })
      const raw = (res.data || []) as any[]

      // Fetch pending counts
      const countsRes = await apiService.getApprovalWorkflowPendingCounts({ documentType: (filters.documenttype || 'PR'), requisitionType: filters.requisitiontype, companyId: selectedCompanyId })
      const countsData = (countsRes.data || []) as any[]
      const map = new Map<string, FlowRowSimple>()
      for (const r of raw) {
        const key = `${String(r.companyid ?? 'null')}|${r.documenttype || 'PR'}|${r.flowname || ''}|${r.requisitiontype}|${r.allocationtype}|${r.facilitytype ?? ''}|${r.cycle ?? ''}|${r.allocationcode ?? ''}`
        if (!map.has(key)) {
          map.set(key, {
            id: key,
            documenttype: ((r.documenttype || 'PR') as 'PR'|'PO'),
            flowname: r.flowname || '',
            requisitiontype: (r.requisitiontype as 'Inventory'|'Service'|'FixedAsset'),
            allocationtype: (r.allocationtype as 'Project'|'CostCenter'),
            companyid: (r.companyid ?? null),
            stages: [] as StageConfig[],
            initiatoruserids: Array.isArray(r.initiatoruserids) ? r.initiatoruserids.map((x: any) => Number(x)).filter((x: number) => Number.isFinite(x)) : [],
            autoposttosap: Boolean(r.autoposttosap),
            facilitytype: r.facilitytype ?? null,
            cycle: r.cycle ?? null,
            allocationcode: r.allocationcode ?? null
          })
        }
        const item = map.get(key) as FlowRowSimple
        const stageUsers: number[] = Array.isArray(r.approveruserids) && r.approveruserids.length ? r.approveruserids.map((x: any) => Number(x)) : (r.approveruserid ? [Number(r.approveruserid)] : [])
        item.stages.push({ name: (r.stagename || r.approverrole) || null, userIds: stageUsers, rule: String(r.stagerule || 'ANY').toUpperCase() as 'ANY'|'ALL', allocationtype: (r.allocationtype as 'Project'|'CostCenter') })
      }
      const rowsUnsorted = Array.from(map.values())
      // Ensure stages are ordered by stepnumber per flow
      const groupedByFlow: Record<string, any[]> = {}
      for (const r of raw) {
        const k = `${String(r.companyid ?? 'null')}|${r.documenttype || 'PR'}|${r.flowname || ''}|${r.requisitiontype}|${r.allocationtype}|${r.facilitytype ?? ''}|${r.cycle ?? ''}|${r.allocationcode ?? ''}`
        if (!groupedByFlow[k]) groupedByFlow[k] = []
        groupedByFlow[k].push(r)
      }
      const finalRows: FlowRowSimple[] = rowsUnsorted.map((row) => {
        const k = `${String(row.companyid ?? 'null')}|${row.documenttype || 'PR'}|${row.flowname || ''}|${row.requisitiontype}|${row.allocationtype}|${row.facilitytype ?? ''}|${row.cycle ?? ''}|${row.allocationcode ?? ''}`
        const rowsForFlow = (groupedByFlow[k] || []).sort((a, b) => Number(a.stepnumber) - Number(b.stepnumber))
        const stages: StageConfig[] = rowsForFlow.map((r: any, idx: number) => {
          const stageUsers: number[] = Array.isArray(r.approveruserids) && r.approveruserids.length ? r.approveruserids.map((x: any) => Number(x)) : (r.approveruserid ? [Number(r.approveruserid)] : [])
          const stepNumber = idx + 1

          // Find matching pending count - sum all counts that match the core criteria
          // Match on: requisitiontype, allocationtype, companyid, flowname, stepnumber
          // Aggregate counts regardless of facilitytype, cycle, allocationcode (workflow-level filters)
          const matchingCounts = countsData.filter((c: any) => {
            const reqTypeMatch = String(c.requisitiontype || '').toUpperCase() === String(row.requisitiontype || '').toUpperCase()
            const allocTypeMatch = String(c.allocationtype || '').toUpperCase() === String(row.allocationtype || '').toUpperCase()
            const companyMatch = String(c.companyid ?? '') === String(row.companyid ?? '')
            const flowMatch = String(c.approvalflowname || '').toUpperCase() === String(row.flowname || '').toUpperCase()
            const stepMatch = Number(c.stepnumber) === stepNumber

            // If workflow has specific allocationcode, only match that code
            // If workflow has null allocationcode, sum all codes
            let allocCodeMatch = true
            if (row.allocationcode) {
              const rowAllocationCodes = parseAllocationCodes(row.allocationcode).map((code) => code.toUpperCase())
              allocCodeMatch = rowAllocationCodes.includes(String(c.allocationcode || '').trim().toUpperCase())
            }

            return reqTypeMatch && allocTypeMatch && companyMatch && flowMatch && stepMatch && allocCodeMatch
          })
          const pendingCount = matchingCounts.reduce((sum, c) => sum + Number(c.pendingcount || 0), 0)

          return {
            name: (r.stagename || r.approverrole) || null,
            userIds: stageUsers,
            rule: String(r.stagerule || 'ANY').toUpperCase() as 'ANY'|'ALL',
            allocationtype: (r.allocationtype as 'Project'|'CostCenter'),
            pendingCount
          }
        })
        const firstRow = rowsForFlow[0] || {}
        return {
          ...row,
          id: buildFlowRowId({
            documenttype: row.documenttype,
            flowname: row.flowname,
            requisitiontype: row.requisitiontype,
            allocationtype: row.allocationtype,
            companyid: row.companyid,
            allocationcode: row.allocationcode ?? firstRow.allocationcode ?? null,
            facilitytype: row.facilitytype ?? firstRow.facilitytype ?? null,
            cycle: row.cycle ?? firstRow.cycle ?? null
          }),
          stages,
          facilitytype: row.facilitytype ?? firstRow.facilitytype ?? null,
          cycle: row.cycle ?? firstRow.cycle ?? null,
          allocationcode: row.allocationcode ?? firstRow.allocationcode ?? null
        }
      })
      setRows(finalRows)
    } catch (e: any) {
      dispatch(showSnackbar({ message: e?.response?.data?.message || 'Failed to load', severity: 'error' }))
    } finally {
      dispatch(setLoading(false))
    }
  }, [dispatch, filters.documenttype, filters.requisitiontype, selectedCompanyId])

  useEffect(() => { load() }, [load])

  useEffect(() => { (async () => { try { const res = await apiService.getUsers(); setAllUsers((res as any)?.data || res || []) } catch {} })() }, [])
  useEffect(() => {
    if (!open) return
    ;(async () => {
      try {
        if (form.allocationtype === 'Project' && !projectOptionsLoadedRef.current) {
          projectOptionsLoadedRef.current = true
          const projRes = await apiService.getProjects()
          const raw = Array.isArray(projRes) ? projRes : (projRes as any)?.data || []
          const list = (raw as any[]).map((p: any) => ({ code: String(p.Code ?? p.ProjCode ?? p.Project ?? ''), name: String(p.Name ?? p.ProjectName ?? '') })).filter((p) => !!p.code)
          setProjectOptions(list)
        }
        if (form.allocationtype === 'CostCenter' && !costCenterOptionsLoadedRef.current) {
          costCenterOptionsLoadedRef.current = true
          const res = await apiService.getDistributionRules(selectedCompanyId, 2)
          const raw = Array.isArray(res) ? res : (res as any)?.data || []
          const list = Array.from(
            new Map(
              (raw as any[])
                .map(getDistributionRuleOption)
                .filter((d): d is { code: string; name: string } => !!d?.code)
                .map((d) => [d.code, d])
            ).values()
          ).sort((a, b) => a.code.localeCompare(b.code))
          setCostCenterOptions(list)
        }
      } catch (e: any) {
        dispatch(showSnackbar({ message: e?.response?.data?.message || 'Failed to load allocation master data', severity: 'error' }))
      }
    })()
  }, [open, form.allocationtype, dispatch, selectedCompanyId])

  const downloadTemplate = async () => {
    try {
      dispatch(setLoading(true))
      const baseURL = process.env.REACT_APP_API_URL || '/api'
      const response = await fetch(`${baseURL}/user-management/approval-workflows/download-template`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })

      if (!response.ok) {
        throw new Error('Failed to download template')
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'approval_workflow_template.xlsx'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      dispatch(showSnackbar({ message: 'Template downloaded successfully', severity: 'success' }))
    } catch (error: any) {
      dispatch(showSnackbar({ message: error.message || 'Failed to download template', severity: 'error' }))
    } finally {
      dispatch(setLoading(false))
    }
  }

  const exportPRWorkflows = async () => {
    try {
      dispatch(setLoading(true))
      await apiService.exportPRWorkflows()
      dispatch(showSnackbar({ message: 'PR workflows exported successfully', severity: 'success' }))
    } catch (error: any) {
      dispatch(showSnackbar({ message: error.message || 'Failed to export PR workflows', severity: 'error' }))
    } finally {
      dispatch(setLoading(false))
    }
  }

  const exportApprovalFlows = async () => {
    try {
      dispatch(setLoading(true))

      // Fetch initiators for all flows
      const initiatorsMap = new Map<string, Array<{ userid: number; fullname: string }>>()

      for (const row of filteredRows) {
        const key = `${row.documenttype || 'PR'}|${row.flowname}|${row.requisitiontype}|${row.allocationtype}|${row.companyid ?? 'null'}`
        try {
          const res = await apiService.listApprovalInitiators({
            documenttype: row.documenttype || 'PR',
            requisitiontype: row.requisitiontype,
            allocationtype: row.allocationtype,
            flowname: row.flowname,
            companyid: row.companyid ?? undefined
          })
          const data = (res as any)?.data || res || []
          const initiatorDetails = data.map((init: any) => {
            const user = allUsers.find(u => Number(u.UserID || u.userid) === Number(init.userid))
            return {
              userid: Number(init.userid),
              fullname: user ? String(user.FullName ?? user.fullname) : `User ${init.userid}`
            }
          })
          initiatorsMap.set(key, initiatorDetails)
        } catch {
          initiatorsMap.set(key, [])
        }
      }

      const csvHeaders = [
        'Flow Name',
        'Module',
        'Allocation Type',
        'Specific Code',
        'Facility Type',
        'Cycle',
        'Auto Post to SAP',
        'Initiators (Name - ID)',
        'Stage Count',
        'Stage 1 - Name',
        'Stage 1 - Rule',
        'Stage 1 - Approvers (Name - ID)',
        'Stage 2 - Name',
        'Stage 2 - Rule',
        'Stage 2 - Approvers (Name - ID)',
        'Stage 3 - Name',
        'Stage 3 - Rule',
        'Stage 3 - Approvers (Name - ID)',
        'Stage 4 - Name',
        'Stage 4 - Rule',
        'Stage 4 - Approvers (Name - ID)',
        'Stage 5 - Name',
        'Stage 5 - Rule',
        'Stage 5 - Approvers (Name - ID)'
      ]

      const csvData = filteredRows.map(row => {
        const moduleLabel = row.requisitiontype === 'Inventory' ? 'Material' : row.requisitiontype === 'Service' ? 'Service' : 'Asset'
        const key = `${row.documenttype || 'PR'}|${row.flowname}|${row.requisitiontype}|${row.allocationtype}|${row.companyid ?? 'null'}`
        const initiators = initiatorsMap.get(key) || []
        const initiatorsText = initiators.length > 0
          ? initiators.map(init => `${init.fullname} (${init.userid})`).join('; ')
          : 'All Users'

        const rowData = [
          row.flowname || '-',
          moduleLabel,
          row.allocationtype,
          row.allocationcode || 'All',
          row.facilitytype || 'NA',
          row.cycle || 'Project',
          row.autoposttosap ? 'Yes' : 'No',
          initiatorsText,
          row.stages.length.toString()
        ]

        // Add up to 5 stages
        for (let i = 0; i < 5; i++) {
          if (i < row.stages.length) {
            const stage = row.stages[i]
            const stageName = stage.name || 'Auto-assigned'
            const stageRule = stage.rule || 'ANY'

            // Get approver details
            const approverUsers = (stage.userIds || [])
              .map(userId => {
                const user = allUsers.find(u => Number(u.UserID || u.userid) === userId)
                return user ? `${String(user.FullName ?? user.fullname)} (${userId})` : `User ${userId}`
              })
            const approversText = approverUsers.length > 0 ? approverUsers.join('; ') : 'Auto-assigned'

            rowData.push(stageName, stageRule, approversText)
          } else {
            rowData.push('-', '-', '-')
          }
        }

        return rowData
      })

      const csvContent = [
        csvHeaders.join(','),
        ...csvData.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `approval_flows_${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      dispatch(showSnackbar({ message: `Exported ${filteredRows.length} approval flows with full details`, severity: 'success' }))
    } catch (error: any) {
      dispatch(showSnackbar({ message: error.message || 'Failed to export approval flows', severity: 'error' }))
    } finally {
      dispatch(setLoading(false))
    }
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0])
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) {
      dispatch(showSnackbar({ message: 'Please select a file', severity: 'error' }))
      return
    }

    try {
      setUploading(true)
      const formData = new FormData()
      formData.append('file', selectedFile)

      const baseURL = process.env.REACT_APP_API_URL || '/api'
      const response = await fetch(`${baseURL}/user-management/approval-workflows/bulk-upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      })

      const result = await response.json()

      if (!result.success) {
        throw new Error(result.message || 'Upload failed')
      }

      const errorCount = result.data?.errors || 0
      const createdCount = result.data?.created || 0
      const errorDetails = result.data?.errorDetails || []

      if (errorCount > 0) {
        dispatch(showSnackbar({
          message: `Upload completed with errors. Created: ${createdCount}, Errors: ${errorCount}. Check console for details.`,
          severity: 'warning'
        }))
        console.error('Upload errors:', errorDetails)
      } else {
        dispatch(showSnackbar({ message: `Successfully uploaded ${createdCount} workflows`, severity: 'success' }))
      }

      setUploadDialogOpen(false)
      setSelectedFile(null)
      await load()
    } catch (error: any) {
      dispatch(showSnackbar({ message: error.message || 'Upload failed', severity: 'error' }))
    } finally {
      setUploading(false)
    }
  }

  const openCreate = () => {
    setEditingFlowKey(null);
    originalFlowIdentifiersRef.current = null;
    setInitiatorUsers([]);
    setForm({
      id: 'draft',
      documenttype: (filters.documenttype as any) || 'PR',
      flowname: '',
      requisitiontype: (filters.requisitiontype as any) || 'Inventory',
      allocationtype: 'Project',
      companyid: selectedCompanyId ?? null,
      stages: [],
      autoposttosap: false,
      facilitytype: 'NA',
      cycle: 'Project',
      allocationcode: null
    });
    setOpen(true)
  }
  const openEdit = (row: FlowRowSimple) => {
    setEditingFlowKey(row.id);
    // Store the original flow identifiers to detect changes
    originalFlowIdentifiersRef.current = {
      documentType: row.documenttype,
      requisitionType: row.requisitiontype,
      allocationType: row.allocationtype,
      flowName: row.flowname,
      companyId: row.companyid ?? null,
      facilityType: row.facilitytype ?? null,
      cycle: row.cycle ?? null,
      allocationCode: row.allocationcode ?? null
    };
    // Ensure all stages match the flow's allocation type
    const normalizedStages = (row.stages || []).map(st => ({
      ...st,
      allocationtype: row.allocationtype
    }));
    setForm({
      id: row.id,
      documenttype: row.documenttype || 'PR',
      flowname: row.flowname,
      requisitiontype: row.requisitiontype,
      allocationtype: row.allocationtype,
      companyid: row.companyid,
      stages: normalizedStages,
      autoposttosap: Boolean(row.autoposttosap),
      facilitytype: row.facilitytype || 'NA',
      cycle: row.cycle || 'Project',
      allocationcode: row.allocationcode || null
    });
    setInitiatorUsers(Array.isArray(row.initiatoruserids) ? row.initiatoruserids : [])
    setOpen(true)
  }
  const close = () => {
    setSaving(false)
    originalFlowIdentifiersRef.current = null
    setEditingFlowKey(null)
    setOpen(false)
  }

  const save = async () => {
    if (saving) return
    try {
      setSaving(true)
      if (!open) return
      const alloc = (form.allocationtype || 'Project') as 'Project'|'CostCenter'
      const group = (form.stages || [])
        .map((st) => ({ ...st, allocationtype: alloc, name: st.name?.trim() || null, userIds: Array.from(new Set((st.userIds || []).map(Number).filter((id) => Number.isFinite(id) && id > 0))) }))
        .filter((st) => st.name || st.userIds.length > 0)
      if (!group.length) {
        dispatch(showSnackbar({ message: 'Add at least one stage', severity: 'warning' }))
        setSaving(false)
        return
      }
      await apiService.saveApprovalWorkflowGroup({
        documenttype: form.documenttype || 'PR',
        requisitiontype: form.requisitiontype,
        allocationtype: alloc,
        flowname: form.flowname?.trim(),
        companyid: form.companyid ?? undefined,
        initiatoruserids: Array.from(new Set(initiatorUsers.map(Number).filter((id) => Number.isFinite(id) && id > 0))),
        autoposttosap: Boolean(form.autoposttosap),
        facilitytype: form.facilitytype || null,
        cycle: form.cycle || null,
        allocationcode: form.allocationcode || null,
        stages: group.map((st) => ({
          name: st.name,
          userIds: st.userIds,
          rule: st.rule || 'ANY'
        })),
        original: originalFlowIdentifiersRef.current ? {
          documenttype: originalFlowIdentifiersRef.current.documentType || 'PR',
          requisitiontype: originalFlowIdentifiersRef.current.requisitionType,
          allocationtype: originalFlowIdentifiersRef.current.allocationType,
          flowname: originalFlowIdentifiersRef.current.flowName,
          companyid: originalFlowIdentifiersRef.current.companyId ?? undefined,
          facilitytype: originalFlowIdentifiersRef.current.facilityType || null,
          cycle: originalFlowIdentifiersRef.current.cycle || null,
          allocationcode: originalFlowIdentifiersRef.current.allocationCode || null
        } : undefined
      })
      dispatch(showSnackbar({ message: 'Flow saved', severity: 'success' }))
      close()
      await load()
    } catch (e: any) {
      dispatch(showSnackbar({ message: e?.response?.data?.message || 'Failed to save', severity: 'error' }))
    } finally {
      setSaving(false)
    }
  }

  const remove = async (row: FlowRowSimple) => {
    try {
      const ok = window.confirm('Delete this approval flow?')
      if (!ok) return
      await apiService.deleteApprovalWorkflowGroup({
        documenttype: row.documenttype || 'PR',
        requisitiontype: row.requisitiontype,
        allocationtype: row.allocationtype,
        flowname: row.flowname,
        companyid: row.companyid ?? undefined,
        facilitytype: row.facilitytype || null,
        cycle: row.cycle || null,
        allocationcode: row.allocationcode || null
      })
      setOpen(false)
      setEditingFlowKey(null)
      setForm({ id: 'draft', documenttype: (filters.documenttype as any) || 'PR', flowname: '', requisitiontype: (filters.requisitiontype as any) || 'Inventory', allocationtype: 'Project', companyid: selectedCompanyId ?? null, stages: [], autoposttosap: false, facilitytype: 'NA', cycle: 'Project', allocationcode: null })
      setInitiatorUsers([])
      dispatch(showSnackbar({ message: 'Flow deleted', severity: 'success' }))
      await load()
    } catch (e: any) {
      dispatch(showSnackbar({ message: e?.response?.data?.message || 'Delete failed', severity: 'error' }))
    }
  }

  const renderStageRow = (idx: number, st: StageConfig) => (
    <Paper sx={{ p: 0.75, mb: 0.75, backgroundColor: '#FFFFFF', borderLeft: `3px solid #0A6ED1`, borderColor: '#E0E0E0', borderWidth: 1, borderStyle: 'solid' }}>
      <Stack spacing={0.75}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Chip label={`Stage ${idx+1}`} size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
          <Stack direction="row" spacing={0.25}>
            <Tooltip title="Move up"><span><IconButton size="small" disabled={idx === 0} onClick={() => setForm(f => { const ns = [...f.stages]; if (idx > 0) { const t = ns[idx-1]; ns[idx-1] = ns[idx]; ns[idx] = t; } return { ...f, stages: ns } })} sx={{ p: 0.5 }}><ArrowUpward sx={{ fontSize: 16 }} /></IconButton></span></Tooltip>
            <Tooltip title="Move down"><span><IconButton size="small" disabled={idx === form.stages.length - 1} onClick={() => setForm(f => { const ns = [...f.stages]; if (idx < ns.length - 1) { const t = ns[idx+1]; ns[idx+1] = ns[idx]; ns[idx] = t; } return { ...f, stages: ns } })} sx={{ p: 0.5 }}><ArrowDownward sx={{ fontSize: 16 }} /></IconButton></span></Tooltip>
            <Tooltip title="Remove"><IconButton size="small" color="error" onClick={() => setForm(f => { const ns = [...f.stages]; ns.splice(idx, 1); return { ...f, stages: ns } })} sx={{ p: 0.4 }}><DeleteOutline sx={{ fontSize: 16 }} /></IconButton></Tooltip>
          </Stack>
        </Stack>
        <Grid container spacing={0.75}>
          <Grid item xs={12}>
            <TextField
              label="Stage Name"
              size="small"
              fullWidth
              value={st.name || ''}
              onChange={(e) => setForm(f => { const ns = [...f.stages]; ns[idx] = { ...ns[idx], name: e.target.value || null }; return { ...f, stages: ns } })}
              placeholder="e.g., ProjectManager"
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <Autocomplete
              multiple
              size="small"
              disableCloseOnSelect
              limitTags={2}
              options={userOptions}
              value={userOptions.filter((option) => (st.userIds || []).includes(option.id))}
              onChange={(_, selected) => setForm(f => {
                const ns = [...f.stages]
                ns[idx] = { ...ns[idx], userIds: selected.map((option) => option.id) }
                return { ...f, stages: ns }
              })}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => option.label}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={checkboxIcon}
                    checkedIcon={checkboxCheckedIcon}
                    checked={selected}
                    size="small"
                    sx={{ mr: 1, py: 0 }}
                  />
                  {option.label}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Approver Users" placeholder="Search users" />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl size="small" fullWidth>
              <InputLabel>Rule</InputLabel>
              <Select
                label="Rule"
                value={st.rule || 'ANY'}
                onChange={(e) => setForm(f => { const ns = [...f.stages]; ns[idx] = { ...ns[idx], rule: (e.target.value as 'ANY'|'ALL') || 'ANY' }; return { ...f, stages: ns } })}
              >
                <MenuItem value="ANY">Any</MenuItem>
                <MenuItem value="ALL">All</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Stack>
    </Paper>
  )

  return (
    <Box
      component="section"
      aria-labelledby="afs-heading"
      sx={{
        width: '100%',
        minWidth: 0,
        p: { xs: 1, sm: 1.5 },
        boxSizing: 'border-box',
        overflowX: 'hidden',
      }}
    >
      <Paper
        sx={{
          width: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
          p: { xs: 1.5, sm: 2 },
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderLeft: '4px solid #0A6ED1',
          boxShadow: theme.shadows[2],
          overflow: 'hidden',
        }}
      >
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: 2, boxShadow: theme.shadows[2], border: '1px solid', borderColor: 'divider' }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
          spacing={{ xs: 1.5, md: 2 }}
          sx={{ minWidth: 0 }}
        >
          <Typography
            component="h1"
            id="afs-heading"
            variant="h5"
            sx={{ fontWeight: 600, fontSize: { xs: 18, sm: 22, md: 24 }, minWidth: 0, flexShrink: 0 }}
          >
            Approval Flow Settings
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{
              minWidth: 0,
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
              overflowX: 'auto',
              pb: 0.25,
              '&::-webkit-scrollbar': { height: 6 },
            }}
          >
            <Button variant="outlined" startIcon={<IosShareIcon />} onClick={exportApprovalFlows}>Export Flows</Button>
            <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportPRWorkflows}>Export All PRs</Button>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadTemplate}>Download Template</Button>
            <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => setUploadDialogOpen(true)}>Bulk Upload</Button>
            <Button variant="contained" startIcon={<AddCircleOutline />} onClick={openCreate}>Add Flow</Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 2, boxShadow: theme.shadows[1], border: '1px solid', borderColor: 'divider' }}>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FilterListIcon color="action" />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Search & Filters
              {Object.values(filters).filter(v => v).length > 0 && (
                <Chip
                  label={`${Object.values(filters).filter(v => v).length} active`}
                  size="small"
                  color="primary"
                  sx={{ ml: 1, height: 20 }}
                />
              )}
            </Typography>
          </Stack>

          {/* Search Box */}
          <TextField
            size="small"
            fullWidth
            placeholder="Search by flow name, allocation code, or stage name..."
            value={filters.searchText || ''}
            onChange={(e) => setFilters(f => ({ ...f, searchText: e.target.value || undefined }))}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
              endAdornment: filters.searchText && (
                <IconButton size="small" onClick={() => setFilters(f => ({ ...f, searchText: undefined }))}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              )
            }}
          />

          {/* Filter Dropdowns */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Autocomplete
                size="small"
                options={documentTypeOptions}
                value={documentTypeOptions.find((option) => option.value === (filters.documenttype || 'PR')) || undefined}
                onChange={(_, option) => setFilters(f => ({ ...f, documenttype: (option?.value as any) || 'PR' }))}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                getOptionLabel={(option) => option.label}
                disableClearable
                renderInput={(params) => (
                  <TextField {...params} label="Document Type" />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Autocomplete
                size="small"
                options={requisitionTypeOptions}
                value={requisitionTypeOptions.find((option) => option.value === filters.requisitiontype) || null}
                onChange={(_, option) => setFilters(f => ({ ...f, requisitiontype: option?.value || undefined }))}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                getOptionLabel={(option) => option.label}
                renderInput={(params) => (
                  <TextField {...params} label="Module" placeholder="All" />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Autocomplete
                size="small"
                options={allocationTypeOptions}
                value={allocationTypeOptions.find((option) => option.value === filters.allocationtype) || null}
                onChange={(_, option) => setFilters(f => ({ ...f, allocationtype: option?.value || undefined }))}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                getOptionLabel={(option) => option.label}
                renderInput={(params) => (
                  <TextField {...params} label="Allocation Type" placeholder="All" />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Autocomplete
                size="small"
                options={facilityTypeOptions}
                value={facilityTypeOptions.find((option) => option.value === filters.facilitytype) || null}
                onChange={(_, option) => setFilters(f => ({ ...f, facilitytype: option?.value || undefined }))}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                getOptionLabel={(option) => option.label}
                renderInput={(params) => (
                  <TextField {...params} label="Facility Type" placeholder="All" />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Autocomplete
                size="small"
                options={cycleOptions}
                value={cycleOptions.find((option) => option.value === filters.cycle) || null}
                onChange={(_, option) => setFilters(f => ({ ...f, cycle: option?.value || undefined }))}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                getOptionLabel={(option) => option.label}
                renderInput={(params) => (
                  <TextField {...params} label="Cycle" placeholder="All" />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Autocomplete
                size="small"
                options={filterSpecificCodeOptions}
                value={filterSpecificCodeOptions.find((option) => option.code === filters.allocationcode) || null}
                onChange={(_, option) => setFilters(f => ({ ...f, allocationcode: option?.code || undefined }))}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                getOptionLabel={(option) => option.label}
                renderInput={(params) => (
                  <TextField {...params} label="Specific Code" placeholder="All" />
                )}
              />
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
            <Typography variant="caption" color="text.secondary">
              Showing {filteredRows.length} of {rows.length} flows
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ClearIcon />}
              onClick={() => setFilters({})}
              disabled={Object.values(filters).filter(v => v).length === 0}
            >
              Clear All Filters
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper sx={{ borderRadius: 2, boxShadow: theme.shadows[1], p: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden', minWidth: 0 }}>
        <Box sx={{ width: '100%', minWidth: 0, overflowX: 'auto', pb: 0.5 }}>
          <DataGrid
            rows={filteredRows}
            getRowId={(row) => row.id}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            autoHeight
            sx={{
              minWidth: 1280,
              '& .MuiDataGrid-cell': {
                py: 1
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: theme.palette.action.hover
              }
            }}
          />
        </Box>
        <Box sx={{ mt: 2 }}>
          <Button startIcon={<AddCircleOutline />} onClick={openCreate}>Add New Flow</Button>
        </Box>
      </Paper>
      </Paper>

      <FioriSidePanel
        open={open}
        onClose={close}
        title={editingFlowKey ? 'Edit Approval Flow' : 'Create Approval Flow'}
        subtitle={editingFlowKey ? 'Modify workflow configuration' : 'Configure approval workflow'}
        width={720}
        actions={
          <>
            <Button
              onClick={close}
              size="small"
              disabled={saving}
              sx={{
                color: '#6A6D70',
                '&:hover': {
                  backgroundColor: '#F0F0F0'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={save}
              disabled={saving || !form.flowname || form.stages.length === 0}
              size="small"
              sx={{
                backgroundColor: '#0A6ED1',
                minWidth: 80,
                '&:hover': {
                  backgroundColor: '#085BAB'
                },
                '&:disabled': {
                  backgroundColor: '#E0E0E0',
                  color: '#9DA0A5'
                }
              }}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </>
        }
      >
        <Stack spacing={1} sx={{ '& > :first-of-type': { mt: 0 } }}>
            {/* Basic Information Section */}
            <Box>
              <Typography variant="caption" sx={{ mt: 0, mb: 0.75, fontWeight: 600, color: '#6A6D70', display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                Basic Information
              </Typography>
              <Paper variant="outlined" sx={{ p: 1, backgroundColor: '#FFFFFF', borderColor: '#E0E0E0' }}>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Flow Name" size="small" fullWidth required value={form.flowname} onChange={(e) => setForm(f => ({ ...f, flowname: e.target.value }))} placeholder="e.g., Material-Project-Flow" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      size="small"
                      options={documentTypeOptions}
                      value={documentTypeOptions.find((option) => option.value === (form.documenttype || 'PR')) || undefined}
                      onChange={(_, option) => setForm(f => ({ ...f, documenttype: (option?.value as any) || 'PR' }))}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      getOptionLabel={(option) => option.label}
                      disableClearable
                      renderInput={(params) => (
                        <TextField {...params} label="Document Type" required />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      size="small"
                      options={requisitionTypeOptions}
                      value={requisitionTypeOptions.find((option) => option.value === form.requisitiontype) || undefined}
                      onChange={(_, option) => setForm(f => ({ ...f, requisitiontype: (option?.value as any) || 'Inventory' }))}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      getOptionLabel={(option) => option.label}
                      disableClearable
                      renderInput={(params) => (
                        <TextField {...params} label="Module Type" required />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      size="small"
                      options={allocationTypeOptions}
                      value={allocationTypeOptions.find((option) => option.value === form.allocationtype) || undefined}
                      onChange={(_, option) => {
                        const newAllocType = (option?.value || 'Project') as 'Project' | 'CostCenter'
                        setForm(f => ({
                          ...f,
                          allocationtype: newAllocType,
                          allocationcode: null,
                          stages: f.stages.map(st => ({ ...st, allocationtype: newAllocType }))
                        }))
                      }}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      getOptionLabel={(option) => option.label}
                      disableClearable
                      renderInput={(params) => (
                        <TextField {...params} label="Allocation Type" required />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {form.allocationtype === 'Project' && (
                      <Autocomplete
                        multiple
                        size="small"
                        disableCloseOnSelect
                        limitTags={2}
                        options={projectOptions}
                        value={projectOptions.filter((p) => selectedAllocationCodes.includes(p.code))}
                        onChange={(_, selected) => setForm(f => ({ ...f, allocationcode: serializeAllocationCodes(selected.map((option) => option.code)) || null }))}
                        isOptionEqualToValue={(option, value) => option.code === value.code}
                        getOptionLabel={(option) => `${option.code}${option.name ? ` - ${option.name}` : ''}`}
                        renderOption={(props, option, { selected }) => (
                          <li {...props}>
                            <Checkbox
                              icon={checkboxIcon}
                              checkedIcon={checkboxCheckedIcon}
                              checked={selected}
                              size="small"
                              sx={{ mr: 1, py: 0 }}
                            />
                            {`${option.code}${option.name ? ` - ${option.name}` : ''}`}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField {...params} label="Specific Project" placeholder="All Projects" helperText="Select one or more projects, or leave blank for all" />
                        )}
                      />
                    )}
                    {form.allocationtype === 'CostCenter' && (
                      <Autocomplete
                        multiple
                        size="small"
                        disableCloseOnSelect
                        limitTags={2}
                        options={costCenterOptions}
                        value={costCenterOptions.filter((c) => selectedAllocationCodes.includes(c.code))}
                        onChange={(_, selected) => setForm(f => ({ ...f, allocationcode: serializeAllocationCodes(selected.map((option) => option.code)) || null }))}
                        isOptionEqualToValue={(option, value) => option.code === value.code}
                        getOptionLabel={(option) => `${option.code}${option.name ? ` - ${option.name}` : ''}`}
                        renderOption={(props, option, { selected }) => (
                          <li {...props}>
                            <Checkbox
                              icon={checkboxIcon}
                              checkedIcon={checkboxCheckedIcon}
                              checked={selected}
                              size="small"
                              sx={{ mr: 1, py: 0 }}
                            />
                            {`${option.code}${option.name ? ` - ${option.name}` : ''}`}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField {...params} label="Specific Cost Center" placeholder="All Cost Centers" helperText="Select one or more cost centers, or leave blank for all" />
                        )}
                      />
                    )}
                  </Grid>
                </Grid>
              </Paper>
            </Box>

            {/* Configuration Section */}
            <Box>
              <Typography variant="caption" sx={{ mb: 0.75, fontWeight: 600, color: '#6A6D70', display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                Settings
              </Typography>
              <Paper variant="outlined" sx={{ p: 1, backgroundColor: '#FFFFFF', borderColor: '#E0E0E0' }}>
                <Grid container spacing={1}>
                  <Grid item xs={6} sm={4}>
                    <Autocomplete
                      size="small"
                      options={[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }]}
                      value={form.autoposttosap ? { value: 'Yes', label: 'Yes' } : { value: 'No', label: 'No' }}
                      onChange={(_, option) => setForm(f => ({ ...f, autoposttosap: option?.value === 'Yes' }))}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      getOptionLabel={(option) => option.label}
                      disableClearable
                      renderInput={(params) => (
                        <TextField {...params} label="Auto Post" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Autocomplete
                      size="small"
                      options={facilityTypeOptions}
                      value={facilityTypeOptions.find((option) => option.value === (form.facilitytype || 'NA')) || undefined}
                      onChange={(_, option) => setForm(f => ({ ...f, facilitytype: option?.value || 'NA' }))}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      getOptionLabel={(option) => option.label}
                      disableClearable
                      renderInput={(params) => (
                        <TextField {...params} label="Facility" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      size="small"
                      options={cycleOptions}
                      value={cycleOptions.find((option) => option.value === (form.cycle || 'Project')) || undefined}
                      onChange={(_, option) => setForm(f => ({ ...f, cycle: option?.value || 'Project' }))}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      getOptionLabel={(option) => option.label}
                      disableClearable
                      renderInput={(params) => (
                        <TextField {...params} label="Cycle" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      size="small"
                      disableCloseOnSelect
                      limitTags={2}
                      options={userOptions}
                      value={userOptions.filter((option) => initiatorUsers.includes(option.id))}
                      onChange={(_, selected) => setInitiatorUsers(selected.map((option) => option.id))}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      getOptionLabel={(option) => option.label}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            icon={checkboxIcon}
                            checkedIcon={checkboxCheckedIcon}
                            checked={selected}
                            size="small"
                            sx={{ mr: 1, py: 0 }}
                          />
                          {option.label}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} label="Initiator Users" placeholder="Search users" />
                      )}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Box>

            {/* Approval Stages Section */}
            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#6A6D70', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                  Approval Stages ({form.stages.length})
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddCircleOutline fontSize="small" />}
                  onClick={() => setForm(f => ({ ...f, stages: [...f.stages, { name: null, userIds: [], rule: 'ANY', allocationtype: f.allocationtype || 'Project' }] }))}
                  sx={{
                    py: 0.4,
                    px: 1.25,
                    fontSize: '0.75rem',
                    backgroundColor: '#0A6ED1',
                    '&:hover': {
                      backgroundColor: '#085BAB'
                    }
                  }}
                >
                  Add Stage
                </Button>
              </Stack>
              {(form.stages || []).length === 0 ? (
                <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', backgroundColor: '#FFFFFF', borderColor: '#E0E0E0' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>No approval stages</Typography>
                  <Button variant="outlined" size="small" startIcon={<AddCircleOutline fontSize="small" />} onClick={() => setForm(f => ({ ...f, stages: [...f.stages, { name: null, userIds: [], rule: 'ANY', allocationtype: f.allocationtype || 'Project' }] }))} sx={{ fontSize: '0.75rem' }}>Add First Stage</Button>
                </Paper>
              ) : (
                <Box sx={{ maxHeight: '350px', overflowY: 'auto', pr: 0.5 }}>
                  {(form.stages || []).map((st, i) => (
                    <Box key={`st-${i}`}>{renderStageRow(i, st)}</Box>
                  ))}
                </Box>
              )}
            </Box>
          </Stack>
      </FioriSidePanel>

      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Upload Approval Workflows</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Upload an Excel file with approval workflow data. Download the template first if you haven't already.
            </Typography>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="workflow-file-input"
            />
            <label htmlFor="workflow-file-input">
              <Button
                variant="outlined"
                component="span"
                fullWidth
                startIcon={<UploadIcon />}
              >
                {selectedFile ? selectedFile.name : 'Select File'}
              </Button>
            </label>
            {selectedFile && (
              <Typography variant="caption" color="text.secondary">
                Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setUploadDialogOpen(false); setSelectedFile(null); }} disabled={uploading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
